/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The one listbox behind a trigger (2026-10-14): a button that opens a list of
 * options in a portal under it — what CustomDropdown and the comparer's
 * FilterSelect both are. Until this hook each carried its own copy of the
 * ARIA and the keyboard, and a fix reached one copy only (FilterSelect's
 * trigger closed its list on Escape, CustomDropdown's never did). Their
 * markup, styling and anchoring stay theirs; the roles, the open state and
 * every key are here.
 *
 * The model — the WAI-ARIA APG listbox pattern, popped up by a button:
 *
 * - The trigger carries `aria-haspopup="listbox"` and `aria-expanded`; a click
 *   toggles. Closed, ArrowDown, ArrowUp, Enter and Space open the list, and
 *   once it has appeared (`onMenuShown`, the menu's opening animation done —
 *   focus moved earlier would scroll to a menu not yet anchored) the selected
 *   option takes the focus, else the first (ArrowUp: the last). Open — a
 *   pointer opened it and the focus stayed on the trigger — Escape closes it
 *   and the arrows go into it.
 * - The options take real DOM focus (no aria-activedescendant: both
 *   components always worked that way). The focused option is the active one,
 *   so there is no index state and an arrow press re-renders nothing.
 *   ArrowDown and ArrowUp step and wrap, Home and End jump to the first and
 *   last; from anywhere else in the menu (a footer) ArrowDown enters at the
 *   first option and ArrowUp at the last. Enter and Space are the option's own
 *   button: a pick hands its value to `onChange`, closes the list and puts the
 *   focus back on the trigger.
 * - Escape closes the list, the focus back on the trigger.
 * - Tab: a menu may carry a footer outside the listbox (a listbox holds
 *   options only), marked `data-menu-footer`; Tab goes from an option to its
 *   control and Shift+Tab back to the selected option. Every other Tab —
 *   Shift+Tab too, from the menu or from the trigger of an open list — closes
 *   the list FROM THE TRIGGER: the focus is put on the trigger and the
 *   browser's own Tab carries on from there, to the control after it (or
 *   before it). The menu sits at the end of <body>, so the browser's Tab from
 *   inside it used to land on the next option of the closing menu and was
 *   lost with it, or on the last control of the page.
 * - A press outside both the trigger and the menu closes the list; that
 *   listener exists only while the list is open.
 * - `multiple` (2026-10-16, the comparer's looptijd): the listbox is
 *   `aria-multiselectable`, an option's `aria-selected` is `isSelected`'s
 *   answer, and a pick hands its value to `onChange` — a toggle the host
 *   resolves — and leaves the list open with the focus on that option.
 *
 * SSR: nothing here runs on the server but the initial `open = false`; the
 * trigger's markup is the component's, and the hook adds no attribute the
 * bake did not already carry.
 */

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/** The options a menu's keys move over. */
const OPTION = '[role="option"]';
/** The control of a menu's footer, outside the listbox. */
const FOOTER_CONTROL = '[data-menu-footer] :is(button, a[href])';

/** Where the focus goes when no option is selected: the first option or the last. */
type Edge = 'first' | 'last';

export interface UseListboxParams {
  /** The selected value: its option says `aria-selected="true"` and takes the focus on opening. */
  value: string;
  /** Called with the value of the option the visitor picks. */
  onChange: (value: string) => void;
  /** Several options may be selected; a pick toggles and keeps the list open. */
  multiple?: boolean;
  /** `multiple` only: whether an option is selected (default: equal to `value`). */
  isSelected?: (optionValue: string) => boolean;
}

export interface Listbox<M extends HTMLElement> {
  open: boolean;
  /** Closes the list and leaves the focus where it is. Stable. */
  close: () => void;
  /** Closes the list and gives the focus back to the trigger. Stable. */
  closeAndFocusTrigger: () => void;
  /** The trigger, for the component's own anchoring. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** The menu: the element the keys are handled on, around the listbox (and a footer). */
  menuRef: React.RefObject<M | null>;
  /** Spread on the trigger button. */
  triggerProps: {
    ref: React.RefObject<HTMLButtonElement | null>;
    'aria-haspopup': 'listbox';
    'aria-expanded': boolean;
    onClick: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  };
  /** Spread on the menu. */
  menuProps: {
    ref: React.RefObject<M | null>;
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  };
  /** Spread on the element that holds the options — the menu itself when it has no footer. */
  listboxProps: { role: 'listbox'; 'aria-multiselectable'?: true };
  /** Spread on the button of the option with this value. */
  optionProps: (optionValue: string) => {
    role: 'option';
    'aria-selected': boolean;
    onClick: () => void;
  };
  /** Call when the opened menu has appeared (motion's `onAnimationComplete`). */
  onMenuShown: () => void;
}

export function useListbox<M extends HTMLElement = HTMLElement>({
  value,
  onChange,
  multiple = false,
  isSelected,
}: UseListboxParams): Listbox<M> {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<M>(null);
  /** Set when the keyboard opened the list: where the focus goes once it has appeared. */
  const focusOnShowRef = useRef<Edge | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const optionsOf = (): HTMLElement[] =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>(OPTION) ?? []);

  /** Focus the selected option; without one, the first or the last. */
  const focusSelected = (edge: Edge) => {
    const options = optionsOf();
    const target =
      options.find((o) => o.getAttribute('aria-selected') === 'true') ??
      (edge === 'last' ? options[options.length - 1] : options[0]);
    target?.focus();
  };

  useEffect(() => {
    if (!open) {
      // A list closed before it had appeared: its exit animation must not take the focus.
      focusOnShowRef.current = null;
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const arrow = e.key === 'ArrowDown' || e.key === 'ArrowUp';
    const edge: Edge = e.key === 'ArrowUp' ? 'last' : 'first';
    if (!open) {
      if (arrow || e.key === 'Enter' || e.key === ' ') {
        // Enter and Space would click the trigger as well: the list opens once.
        e.preventDefault();
        focusOnShowRef.current = edge;
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (arrow) {
      e.preventDefault();
      focusOnShowRef.current = null;
      focusSelected(edge);
    } else if (e.key === 'Tab') {
      // The list does not stay open behind the focus; the browser's Tab moves on.
      setOpen(false);
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeAndFocusTrigger();
      return;
    }
    const options = optionsOf();
    const active = document.activeElement as HTMLElement | null;
    const index = active ? options.indexOf(active) : -1;
    if (e.key === 'Tab') {
      const footer = menuRef.current?.querySelector<HTMLElement>(FOOTER_CONTROL) ?? null;
      if (footer && !e.shiftKey && index >= 0) {
        e.preventDefault();
        footer.focus();
        return;
      }
      if (footer && e.shiftKey && active === footer) {
        e.preventDefault();
        focusSelected('first');
        return;
      }
      // Not prevented: with the focus on the trigger, the browser's Tab moves on from there.
      closeAndFocusTrigger();
      return;
    }
    if (!options.length) return;
    const last = options.length - 1;
    let next: number;
    switch (e.key) {
      case 'ArrowDown':
        next = index < 0 ? 0 : (index + 1) % options.length;
        break;
      case 'ArrowUp':
        next = index < 0 ? last : (index - 1 + options.length) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    e.preventDefault();
    options[next].focus();
  };

  const onMenuShown = () => {
    const edge = focusOnShowRef.current;
    if (!edge) return;
    focusOnShowRef.current = null;
    focusSelected(edge);
  };

  return {
    open,
    close,
    closeAndFocusTrigger,
    triggerRef,
    menuRef,
    triggerProps: {
      ref: triggerRef,
      'aria-haspopup': 'listbox',
      'aria-expanded': open,
      onClick: () => setOpen((o) => !o),
      onKeyDown: onTriggerKeyDown,
    },
    menuProps: { ref: menuRef, onKeyDown: onMenuKeyDown },
    listboxProps: multiple
      ? { role: 'listbox', 'aria-multiselectable': true }
      : { role: 'listbox' },
    optionProps: (optionValue) => ({
      role: 'option',
      'aria-selected': multiple && isSelected ? isSelected(optionValue) : optionValue === value,
      onClick: () => {
        onChange(optionValue);
        if (!multiple) closeAndFocusTrigger();
      },
    }),
    onMenuShown,
  };
}
